import { styled } from '@mui/material/styles'
import {
  Avatar as MuiAvatar,
  Box,
  Button,
  Grid,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { PrivatePage } from '../component/PagePrivate'
import { t } from 'i18next'

import sprite_ from '../sass/sprite.module.sass'
import React from 'react'
import { Add as AddIcon } from '@mui/icons-material'
import { DialogAddRestrictions } from '../container/settings/DialogAddRestrictions'
import { api } from '../function/axios'
import DeleteIcon from '@mui/icons-material/Delete'
import ToolsSettingsForm from './system_page/ToolsSettingsForm'
import ModelSelectorCard from './system_page/ModelSelectorCard'
import PromptsPanel from './system_page/PromptsPanel'
import KnowledgeBasePanel from './system_page/KnowledgeBasePanel'

import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { BlackListTableEmails } from '../container/dashboard/BlackListEmails'
import { BlackListTablePhones } from '../container/dashboard/BlackListPhones'
import { BlackListTableIpAddress } from '../container/dashboard/BlackListIpAddress'
import IrbiModelSelector from './system_page/IrbiModelSelector'
import IrbiPromptsPanel from './system_page/IrbiPromptsPanel'
import IrbiKnowledgeBasePanel from './system_page/IrbiKnowledgeBasePanel'
import PdfReportPrompt from './system_page/PdfReportPrompt'

const renderToggleRow = (
  checked: boolean,
  source_name: string,
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  extra?: React.ReactNode,
) => (
  <Grid container alignItems="center" spacing={1} style={{ padding: '5px 5px' }}>
    <Grid item xs={2}>
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Box ml={3} className={sprite_['social__small_' + source_name]} />
      </Box>
    </Grid>

    <Grid item xs={2}>
      <Box display="flex" alignItems="center">
        <Switch color="primary" checked={checked} onChange={onChange} />
        <Box>{source_name}</Box>
      </Box>
    </Grid>

    <Grid item xs={2}>
      <Box display="flex" alignItems="center">
        {extra}
      </Box>
    </Grid>
  </Grid>
)

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

const returnNameByType = (type: string) => {
  switch (type) {
    case 'combined_phone':
      return 'Phone/Advanced phone:'
      break
    default:
      break
  }
}

export function SystemPage() {
  const [toggleStates, setToggleStates] = React.useState<Record<string, boolean>>({})

  const [allRestrictions, setRestrictions] = React.useState<any[]>([])

  const handleClickDeleteRestrictions = async (type: string) => {
    try {
      const response = await api({
        method: 'DELETE',
        url: `/admin_settings/restriction?type=${encodeURIComponent(type)}`,
      })
      setRestrictions((prev) =>
        prev.filter((restriction: any) => restriction.type !== type),
      )

    } catch (error) {
      console.error('Failed to fetch maintenance status:', error)
    }
  }

  const updateRestriction = async (type: string, source: string, value: boolean) => {
    try {
      const response = await api({
        method: 'PUT',
        url: '/admin_settings/restriction',
        body: {
          type: type,
          source: source,
          value: value,
        },
      })

    } catch (error) {
      console.error('Failed to fetch maintenance status:', error)
    }
  }

  React.useEffect(() => {
    let cancelled = false
  
    const load = async () => {
      try {
        const { data } = await api({
          method: 'GET',
          url: '/admin_settings/restrictions',
        })
        if (!cancelled) setRestrictions(data)
      } catch (error) {
        console.error('Failed to fetch restrictions:', error)
      }
    }
  
    load()
  
    return () => { cancelled = true }
  }, [])


  React.useEffect(() => {
    const initialStates: Record<string, boolean> = {}
    allRestrictions.forEach(({ source, status }) => {
      initialStates[source] = status
    })
    setToggleStates(initialStates)
  }, [])

  const handleToggleChange = (type: string, source: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    updateRestriction(type, source, event.target.checked)
    setToggleStates((prev) => ({
      ...prev,
      [source]: event.target.checked,
    }))
  }

  const [restrictionsOpen, setRestrictionsdOpen] = React.useState(false)

  const handleClickRestrictions = () => {
    setRestrictionsdOpen(true)
  }

  const [value, setValue] = React.useState(4)

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  return (
    <PrivatePage>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
          <Tab label={t('system.title')} {...a11yProps(0)}/>
          <Tab label="AI Support Chat Configuration" {...a11yProps(1)} />
          <Tab label="IRBI" {...a11yProps(2)} />
          <Tab label="Blacklists" {...a11yProps(3)} />
          <Tab label="PDF Report" {...a11yProps(4)} />
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <Stack direction="row" justifyContent="center" width="100%">
          <Button
            variant="contained"
            color="primary"
            style={{
              width: 40,
              height: 40,
              minWidth: 0,
              borderRadius: '50%',
              padding: 0,
              alignItems: 'left',
            }}
            onClick={handleClickRestrictions}
          >
            <AddIcon style={{
              width: 20,
              height: 20,
            }} />
          </Button>
        </Stack>

        <Stack direction="column" spacing={2} width="100%">
          {allRestrictions.map((restriction: any) => (
            <Stack key={restriction.type} spacing={1}>

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                  {returnNameByType(restriction.type)}
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  style={{
                    width: 40,
                    height: 40,
                    minWidth: 0,
                    borderRadius: '50%',
                    padding: 0,
                    alignItems: 'left',
                  }}
                  onClick={() => handleClickDeleteRestrictions(restriction.type)}
                >
                  <DeleteIcon style={{
                    width: 20,
                    height: 20,
                  }} />
                </Button>
              </Stack>

              <Grid container spacing={2}>
                {restriction.sources.map((source: any) => (
                  <Grid item xs={3} key={source.source}>
                    {renderToggleRow(
                      toggleStates[source.source] ?? source.status ?? false,
                      source.source,
                      handleToggleChange(restriction.type, source.source),
                    )}
                  </Grid>
                ))}
              </Grid>
            </Stack>
          ))}
        </Stack>
        {restrictionsOpen && (
          <DialogAddRestrictions
            onClose={() => setRestrictionsdOpen(false)}
          />
        )}
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <Grid container spacing={0.5} width="100%">
          <Grid item xs={8}>
            <ToolsSettingsForm />
          </Grid>

          <Grid item xs={4}>
            <ModelSelectorCard />
          </Grid>

          <Grid item xs={12}>
            <PromptsPanel />
          </Grid>

          <Grid item xs={12}>
            <KnowledgeBasePanel />
          </Grid>
        </Grid>
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
      <Grid container spacing={0.5} width="100%">

          <Grid item xs={4}>
            <IrbiModelSelector />
          </Grid>
          <Grid item xs={8}>
            <IrbiKnowledgeBasePanel />
          </Grid>
          <Grid item xs={12}>
            <IrbiPromptsPanel />
          </Grid>


        </Grid>
      </CustomTabPanel>
      <CustomTabPanel value={value} index={3}>
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <BlackListTableEmails />
          </Grid>

          <Grid item xs={4}>
            <BlackListTablePhones />
          </Grid>

          <Grid item xs={4}>
            <BlackListTableIpAddress />
          </Grid>
        </Grid>
      </CustomTabPanel>
      <CustomTabPanel value={value} index={4}>
        <Box sx={{ width: "150vh", height: "70vh" }}>
          <PdfReportPrompt />
        </Box>
      </CustomTabPanel>
    </PrivatePage>

  )
}
